import { TextStyle } from 'pixi.js'
import { useCallback, useEffect, useRef } from 'react'
import { Container, Graphics, Text } from '@pixi/react'
import { Draw } from '../../type/PixiType'
import { Button, FixedContainerV2 } from '../../UI'
import { leaveGameToLobby } from '../../domain/leaveGame/leaveGameUtil'
import { usePlayerStore } from '../../domain/player/store/playerStore'
import { t } from '../../domain/language/languageUtil'
import { useSessionLimitStore } from '../../domain/sessionLimit/store/sessionLimitStore'

// remaining ratio at/below this triggers the "approaching limit" heads-up
const WARNING_REMAINING_RATIO = 0.1

interface SessionLimitPopupProps {
  gameWidth: number
  gameHeight: number
  /**
   * Whether the current round (including any free game / bonus game evolutions) is
   * fully settled. The loss limit and the forced-exit popup must only be evaluated
   * / shown while this is true, so a session-limit breach never interrupts a round
   * in progress.
   */
  isRoundSettled: boolean
  width?: number
  height?: number
  /** 確認按鈕的尺寸，預設 270x84。畫面邏輯寬度比彈窗預設寬度（816）窄很多的原生直向遊戲，
   * 應該連同 width/height 一起等比例縮小，否則按鈕會相對彈窗顯得過大 */
  confirmButtonWidth?: number
  confirmButtonHeight?: number
}

export function SessionLimitPopup({ gameWidth, gameHeight, isRoundSettled, width = 816, height = 515, confirmButtonWidth = 270, confirmButtonHeight = 84 }: SessionLimitPopupProps) {
  const x = (gameWidth - width) / 2
  const y = (gameHeight - height) / 2

  const timeLimitMs = useSessionLimitStore(state => state.timeLimitMs)
  const isShowWarning = useSessionLimitStore(state => state.isShowWarning)
  const isShowForcedPopup = useSessionLimitStore(state => state.isShowForcedPopup)
  const reachedReason = useSessionLimitStore(state => state.reachedReason)
  // sessionWinLose only reflects the true net position once the round is fully settled —
  // mid-round it can briefly hold the stake deduction without its payout, so only trust it
  // for the loss check once the round is settled (guarded inside evaluate() via isRoundSettled).
  const sessionWinLose = usePlayerStore(state => state.sessionWinLose)

  const isRoundSettledRef = useRef(isRoundSettled)
  useEffect(() => {
    isRoundSettledRef.current = isRoundSettled
  }, [isRoundSettled])

  const evaluate = useCallback((isSettled: boolean) => {
    const state = useSessionLimitStore.getState()
    if (state.timeLimitMs <= 0 && state.maxLoss <= 0) return

    if (state.isLimitReached) {
      if (!state.isShowForcedPopup && isSettled) {
        state.showForcedPopup()
      }
      return
    }

    const elapsed = Date.now() - state.sessionStartAt
    const loss = isSettled ? Math.max(0, -usePlayerStore.getState().sessionWinLose) : 0

    const timeReached = state.timeLimitMs > 0 && elapsed >= state.timeLimitMs
    const lossReached = isSettled && state.maxLoss > 0 && loss >= state.maxLoss
    if (timeReached || lossReached) {
      state.markLimitReached(timeReached ? 'time' : 'loss')
      // show the forced popup in the same pass, not on some later re-check — we already know
      // right now whether the round is settled, no reason to wait for the next tick/event
      if (isSettled) {
        state.showForcedPopup()
      }
      return
    }

    const timeRemainingRatio = state.timeLimitMs > 0 ? 1 - elapsed / state.timeLimitMs : 1
    const lossRemainingRatio = isSettled && state.maxLoss > 0 ? 1 - loss / state.maxLoss : 1
    if (!state.hasWarned && (timeRemainingRatio <= WARNING_REMAINING_RATIO || lossRemainingRatio <= WARNING_REMAINING_RATIO)) {
      state.showWarning()
    }
  }, [])

  // Event-driven: the loss limit only ever changes as a result of sessionWinLose changing or
  // a round settling, so re-check immediately when either does, instead of waiting for a poll.
  useEffect(() => {
    evaluate(isRoundSettled)
  }, [sessionWinLose, isRoundSettled, evaluate])

  // The time limit has no discrete change event to key off — elapsed time just keeps ticking —
  // so it still needs polling. Loss doesn't need this timer at all now; only set it up when a
  // time limit is actually configured. Reads isRoundSettledRef, not the isRoundSettled prop
  // directly, so each tick sees the current value instead of whatever it was when the timer
  // was created.
  useEffect(() => {
    if (timeLimitMs <= 0) return
    const timer = setInterval(() => evaluate(isRoundSettledRef.current), 1000)
    return () => clearInterval(timer)
  }, [timeLimitMs, evaluate])

  const backgroundDraw = useCallback<Draw>((background) => {
    background.clear()
    background.beginFill('#0D0A02')
    background.drawRoundedRect(0, 0, width - 4, height, 10)
  }, [height, width])

  const borderDraw = useCallback<Draw>((border) => {
    const borderWidth = 2
    const borderRadius = 10
    border.clear()
    border.lineStyle(borderWidth, '#DBD2CA', 1)
    border.drawRoundedRect(-borderWidth, 0, width - borderWidth, height - borderWidth, borderRadius)
  }, [height, width])

  if (!isShowForcedPopup && !isShowWarning) return null

  const reachedKey = reachedReason === 'time' ? 'session_limit_reached_time' : 'session_limit_reached_loss'
  const message = isShowForcedPopup ? t(reachedKey) : t('session_limit_warning')

  const onConfirm = () => {
    if (isShowForcedPopup) {
      leaveGameToLobby()
    }
    else {
      useSessionLimitStore.setState({ isShowWarning: false })
    }
  }

  return (
    <FixedContainerV2 name="SessionLimitPopup" width={gameWidth} height={gameHeight} background="0x000000" backgroundAlpha={0.6} blockEvents={true} eventMode="static">
      <Container position={[x, y]}>
        <Graphics draw={backgroundDraw} />
        <Graphics draw={borderDraw} />

        <Text
          text={message}
          x={width / 2}
          y={height * 0.4}
          anchor={0.5}
          style={
            new TextStyle({
              fontSize: 40,
              fontWeight: 'bold',
              fontFamily: 'Arial',
              fill: '#F0E1D4',
              align: 'center',
              wordWrap: true,
              breakWords: true,
              wordWrapWidth: width * 0.8,
              lineHeight: 50,
            })
          }
        />

        <Button
          name="SessionLimitConfirmButton"
          x={(width - confirmButtonWidth) / 2}
          y={height * (380 / 515)}
          width={confirmButtonWidth}
          height={confirmButtonHeight}
          onPress={onConfirm}
          defaultView="error_popup_btn_normal"
          hoverView="error_popup_btn_hover"
          pressedView="error_popup_btn_hover"
        />
        <Text
          text={isShowForcedPopup ? t('session_limit_leave_btn') : t('error_popup_ok')}
          x={width / 2}
          y={height * (380 / 515) + confirmButtonHeight / 2}
          anchor={0.5}
          eventMode="passive"
          style={
            new TextStyle({
              fontSize: confirmButtonHeight * (28 / 84),
              fontWeight: 'bold',
              fontFamily: 'Arial',
              fill: '#F0E1D4',
              align: 'center',
              wordWrap: true,
              breakWords: true,
              wordWrapWidth: confirmButtonWidth * (250 / 270),
            })
          }
        />
      </Container>
    </FixedContainerV2>
  )
}
